import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminAPI } from '../../utils/api';

const fmt = (secs) => {
  if (!secs) return '0m';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const Bar = ({ pct, color = '#6B4EFF' }) => (
  <div style={{ background: '#F1F5F9', borderRadius: '10px', height: '8px', flex: 1 }}>
    <div style={{ background: color, borderRadius: '10px', height: '100%', width: `${Math.min(pct, 100)}%`, transition: 'width 0.5s' }}></div>
  </div>
);

const ReadinessColor = (score) => {
  if (score >= 80) return { bg: '#E6F4EC', color: '#1B7F3E' };
  if (score >= 60) return { bg: '#E8F0FB', color: '#1A3C6E' };
  if (score >= 40) return { bg: '#FEF3C7', color: '#B45309' };
  return { bg: '#FEE2E2', color: '#DC2626' };
};

const AdminComparisonPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('schools');
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [readinessData, setReadinessData] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [velocityData, setVelocityData] = useState(null);
  const [velocityLoading, setVelocityLoading] = useState(false);
  const [atRiskData, setAtRiskData] = useState(null);
  const [atRiskLoading, setAtRiskLoading] = useState(false);
  const [schools, setSchools] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [compRes, schoolsRes] = await Promise.all([
        adminAPI.getComparison(),
        adminAPI.getSchools()
      ]);
      setData(compRes.data.data);
      setSchools(schoolsRes.data.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadVelocity = async (schoolId) => {
    setVelocityLoading(true);
    try {
      const res = await adminAPI.getLearningVelocity(schoolId);
      setVelocityData(res.data.data);
    } catch(e) { console.error(e); }
    finally { setVelocityLoading(false); }
  };

  const loadAtRisk = async () => {
    setAtRiskLoading(true);
    try {
      const res = await adminAPI.getAtRiskStudents();
      setAtRiskData(res.data.data);
    } catch(e) { console.error(e); }
    finally { setAtRiskLoading(false); }
  };

  const loadReadiness = async (schoolId) => {
    setReadinessLoading(true);
    try {
      const res = await adminAPI.getExamReadiness(schoolId);
      setReadinessData(res.data.data);
    } catch(e) { console.error(e); }
    finally { setReadinessLoading(false); }
  };

  const tabs = ['schools', 'classes', 'subjects', 'content', 'peak hours', 'exam readiness', 'at risk', 'velocity'];

  return (
    <AdminShell title="Comparison Analytics" subtitle="Compare schools, classes, subjects and exam readiness">
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#F7F8FC', borderRadius: '10px', padding: '4px', flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize', background: activeTab === t ? 'white' : 'transparent', color: activeTab === t ? '#6B4EFF' : '#64748B', boxShadow: activeTab === t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748B' }}>Loading comparison data...</div>
      ) : (
        <>
          {/* School vs School */}
          {activeTab === 'schools' && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '13px', fontWeight: 800 }}>School Performance Comparison</div>
                <div style={{ fontSize: '11px', color: '#94A3B8' }}>Ranked by Avg Mastery Score</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F7F8FC' }}>
                    {['#', 'School', 'Students', 'Active This Week', 'Avg Mastery', 'Avg Daily Study', 'Score'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.schools?.length > 0 ? data.schools.map((s, i) => {
                    const maxMastery = Math.max(...data.schools.map(x => x.avg_mastery));
                    const pct = maxMastery > 0 ? (s.avg_mastery / maxMastery) * 100 : 0;
                    return (
                      <tr key={s.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 800, color: i === 0 ? '#FF6B00' : '#94A3B8' }}>#{i + 1}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</div>
                          <div style={{ fontSize: '11px', color: '#64748B' }}>{s.city}</div>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px' }}>{s.total_students}</td>
                        <td style={{ padding: '12px 14px', fontSize: '13px' }}>{s.active_this_week}</td>
                        <td style={{ padding: '12px 14px', minWidth: '150px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Bar pct={pct} color={i === 0 ? '#FF6B00' : '#6B4EFF'} />
                            <span style={{ fontSize: '12px', fontWeight: 700, minWidth: '35px' }}>{Math.round(s.avg_mastery)}%</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px' }}>{fmt(s.avg_daily_seconds)}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: s.avg_mastery >= 70 ? '#E6F4EC' : s.avg_mastery >= 40 ? '#FEF3C7' : '#FEE2E2', color: s.avg_mastery >= 70 ? '#1B7F3E' : s.avg_mastery >= 40 ? '#B45309' : '#DC2626' }}>
                            {s.avg_mastery >= 70 ? 'Excellent' : s.avg_mastery >= 40 ? 'Average' : 'Needs Attention'}
                          </span>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>No school data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Class vs Class */}
          {activeTab === 'classes' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '16px' }}>Class-wise National Comparison</div>
                {data?.class_comparison?.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {data.class_comparison.map((c, i) => {
                      const maxMastery = Math.max(...data.class_comparison.map(x => x.avg_mastery));
                      const pct = maxMastery > 0 ? (c.avg_mastery / maxMastery) * 100 : 0;
                      return (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 100px 120px 80px', alignItems: 'center', gap: '16px' }}>
                          <div style={{ fontSize: '14px', fontWeight: 800, color: '#6B4EFF' }}>Class {c.class_id}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Bar pct={pct} />
                            <span style={{ fontSize: '12px', fontWeight: 700, minWidth: '35px' }}>{Math.round(c.avg_mastery)}%</span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748B' }}>{c.total_students} students</div>
                          <div style={{ fontSize: '12px', color: '#64748B' }}>{fmt(c.avg_daily_seconds)}/day avg</div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: '#1B7F3E' }}>{c.active_today} today</div>
                        </div>
                      );
                    })}
                  </div>
                ) : <div style={{ color: '#94A3B8', fontSize: '13px' }}>No class data yet</div>}
              </div>
            </div>
          )}

          {/* Subject Difficulty */}
          {activeTab === 'subjects' && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>Subject Difficulty Index</div>
              <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '16px' }}>Lower score = harder subject nationally</div>
              {data?.subject_difficulty?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {data.subject_difficulty.map((s, i) => {
                    const color = s.avg_score >= 70 ? '#1B7F3E' : s.avg_score >= 50 ? '#B45309' : '#DC2626';
                    const bg = s.avg_score >= 70 ? '#E6F4EC' : s.avg_score >= 50 ? '#FEF3C7' : '#FEE2E2';
                    return (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 80px 80px 100px', alignItems: 'center', gap: '14px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, textTransform: 'capitalize' }}>{s.subject}</div>
                        <Bar pct={s.avg_score} color={color} />
                        <span style={{ fontSize: '12px', fontWeight: 700 }}>{Math.round(s.avg_score)}% avg</span>
                        <span style={{ fontSize: '11px', color: '#94A3B8' }}>{s.attempts} attempts</span>
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: bg, color, textAlign: 'center' }}>
                          {s.avg_score >= 70 ? 'Easy' : s.avg_score >= 50 ? 'Medium' : 'Hard'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : <div style={{ color: '#94A3B8', fontSize: '13px' }}>No subject data yet</div>}
            </div>
          )}

          {/* Content Quality Flags */}
          {activeTab === 'content' && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '13px', fontWeight: 800 }}>⚠️ Content Quality Flags</div>
                <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>Topics where 60%+ students score below 50% — needs content review</div>
              </div>
              {data?.content_flags?.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#FFF5F5' }}>
                      {['Topic ID', 'Attempts', 'Avg Score', 'Struggling Students', 'Action'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.content_flags.map((f, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: 700, color: '#6B4EFF' }}>{f.topic_id}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px' }}>{f.attempts}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#DC2626' }}>{Math.round(f.avg_score)}%</span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: '12px', color: '#DC2626', fontWeight: 700 }}>{f.low_score_count} of {f.attempts}</span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', background: '#FEE2E2', color: '#DC2626' }}>Review Content</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px', color: '#94A3B8', fontSize: '13px' }}>
                  ✅ No content quality issues detected yet
                </div>
              )}
            </div>
          )}

          {/* Peak Hours */}
          {activeTab === 'peak hours' && (
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>Peak Study Hours (Last 7 Days)</div>
              <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '20px' }}>When students study the most — use this for scheduling reminders</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '140px' }}>
                {Array.from({ length: 24 }, (_, hour) => {
                  const hourData = data?.peak_hours?.find(h => h.hour === hour);
                  const maxSessions = Math.max(...(data?.peak_hours?.map(h => h.sessions) || [1]));
                  const pct = hourData ? (hourData.sessions / maxSessions) * 100 : 0;
                  const isPeak = pct > 60;
                  return (
                    <div key={hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      {hourData && <div style={{ fontSize: '8px', color: '#64748B', fontWeight: 700 }}>{hourData.unique_users}</div>}
                      <div style={{ width: '100%', height: `${Math.max(pct * 1.1, 4)}px`, background: isPeak ? '#FF6B00' : '#6B4EFF', borderRadius: '3px', opacity: isPeak ? 1 : 0.5, minHeight: '4px' }}></div>
                      <div style={{ fontSize: '8px', color: '#94A3B8', transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>{hour}h</div>
                    </div>
                  );
                })}
              </div>
              {data?.peak_hours?.length > 0 && (
                <div style={{ marginTop: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  {(() => {
                    const sorted = [...(data?.peak_hours || [])].sort((a, b) => b.sessions - a.sessions);
                    const top3 = sorted.slice(0, 3);
                    return top3.map((h, i) => (
                      <div key={i} style={{ background: '#F7F8FC', borderRadius: '8px', padding: '10px 14px', fontSize: '12px' }}>
                        <span style={{ fontWeight: 800, color: '#FF6B00' }}>{h.hour}:00 – {h.hour + 1}:00</span>
                        <span style={{ color: '#64748B', marginLeft: '8px' }}>{h.sessions} sessions · {h.unique_users} students</span>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Exam Readiness */}
          {activeTab === 'exam readiness' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>Exam Readiness Score</div>
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '16px' }}>Select a school to see student-wise readiness. Score based on mastery (40%) + revision compliance (30%) + topic coverage (20%) + recent activity (10%)</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {schools.filter(s => s.status === 'approved').map(s => (
                    <button key={s.id} onClick={() => { setSelectedSchool(s); loadReadiness(s.id); }}
                      style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: selectedSchool?.id === s.id ? 'none' : '1px solid #E2E8F0', background: selectedSchool?.id === s.id ? '#6B4EFF' : 'white', color: selectedSchool?.id === s.id ? 'white' : '#64748B', cursor: 'pointer' }}>
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>

              {selectedSchool && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', fontSize: '13px', fontWeight: 800 }}>
                    {selectedSchool.name} — Student Exam Readiness
                  </div>
                  {readinessLoading ? (
                    <div style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>Loading...</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F7F8FC' }}>
                          {['Student', 'Class', 'Mastery', 'Revision Rate', 'Topics', 'Readiness Score', 'Status'].map(h => (
                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {readinessData?.length > 0 ? readinessData.map((s, i) => {
                          const rc = ReadinessColor(s.exam_readiness);
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                              <td style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#F3F0FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: '#6B4EFF' }}>
                                    {s.name?.charAt(0).toUpperCase()}
                                  </div>
                                  <span style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</span>
                                </div>
                              </td>
                              <td style={{ padding: '10px 14px', fontSize: '12px' }}>Class {s.class_id}</td>
                              <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: 700 }}>{Math.round(s.avg_mastery)}%</td>
                              <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: 700 }}>{s.revision_rate}%</td>
                              <td style={{ padding: '10px 14px', fontSize: '12px' }}>{s.topics_studied} studied · {s.topics_mastered} mastered</td>
                              <td style={{ padding: '10px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{ flex: 1, background: '#F1F5F9', borderRadius: '10px', height: '8px', maxWidth: '80px' }}>
                                    <div style={{ background: s.exam_readiness >= 60 ? '#1B7F3E' : s.exam_readiness >= 40 ? '#B45309' : '#DC2626', borderRadius: '10px', height: '100%', width: `${s.exam_readiness}%` }}></div>
                                  </div>
                                  <span style={{ fontSize: '13px', fontWeight: 800 }}>{s.exam_readiness}</span>
                                </div>
                              </td>
                              <td style={{ padding: '10px 14px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: rc.bg, color: rc.color }}>
                                  {s.readiness_label}
                                </span>
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr><td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>No student data yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
          {/* At Risk Tab */}
          {activeTab === 'at risk' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 800 }}>⚠️ At Risk Students — Platform Wide</div>
                  <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>Students inactive 3+ days, 5+ overdue revisions, or avg quiz below 40%</div>
                </div>
                <button onClick={loadAtRisk} style={{ background: '#6B4EFF', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                  Load At Risk Students
                </button>
              </div>
              {atRiskLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>Loading...</div>
              ) : atRiskData && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#FFF5F5' }}>
                        {['Student', 'School', 'Class', 'Last Active', 'Overdue', 'Avg Score', 'Risk Level', 'Reasons'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {atRiskData.length > 0 ? atRiskData.map((s, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 700 }}>{s.name}</td>
                          <td style={{ padding: '10px 12px', fontSize: '11px', color: '#64748B' }}>{s.school_name || '—'}</td>
                          <td style={{ padding: '10px 12px', fontSize: '12px' }}>Class {s.class_id}</td>
                          <td style={{ padding: '10px 12px', fontSize: '12px', color: '#DC2626', fontWeight: 700 }}>
                            {s.days_inactive ? s.days_inactive + ' days ago' : 'Never'}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '12px', fontWeight: 700, color: s.overdue_revisions > 0 ? '#DC2626' : '#94A3B8' }}>{s.overdue_revisions}</td>
                          <td style={{ padding: '10px 12px', fontSize: '12px', fontWeight: 700, color: s.avg_quiz_score < 40 ? '#DC2626' : '#1B7F3E' }}>{Math.round(s.avg_quiz_score)}%</td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '20px', background: s.risk_level === 'high' ? '#FEE2E2' : '#FEF3C7', color: s.risk_level === 'high' ? '#DC2626' : '#B45309' }}>
                              {s.risk_level === 'high' ? '🔴 High' : '🟡 Medium'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '11px', color: '#64748B' }}>{s.risk_reasons?.join(' · ')}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>✅ No at-risk students found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Learning Velocity Tab */}
          {activeTab === 'velocity' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '4px' }}>Learning Velocity</div>
                <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '16px' }}>How fast students are progressing — topics studied this week vs last week</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {schools.filter(s => s.status === 'approved').map(s => (
                    <button key={s.id} onClick={() => { setSelectedSchool(s); loadVelocity(s.id); }}
                      style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: selectedSchool?.id === s.id ? 'none' : '1px solid #E2E8F0', background: selectedSchool?.id === s.id ? '#6B4EFF' : 'white', color: selectedSchool?.id === s.id ? 'white' : '#64748B', cursor: 'pointer' }}>
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>

              {velocityLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>Loading...</div>
              ) : velocityData && (
                <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#F7F8FC' }}>
                        {['Student', 'Class', 'This Week', 'Last Week', 'Trend', 'vs Class Avg', 'Total Topics', 'Avg Daily'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {velocityData.length > 0 ? velocityData.map((s, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#F3F0FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800, color: '#6B4EFF' }}>
                                {s.name?.charAt(0).toUpperCase()}
                              </div>
                              <span style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</span>
                            </div>
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '12px' }}>Class {s.class_id}</td>
                          <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 800, color: '#6B4EFF' }}>{s.topics_this_week}</td>
                          <td style={{ padding: '10px 12px', fontSize: '12px', color: '#94A3B8' }}>{s.topics_last_week}</td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: s.trend === 'improving' ? '#1B7F3E' : s.trend === 'declining' ? '#DC2626' : '#94A3B8' }}>
                              {s.trend === 'improving' ? '↑ Improving' : s.trend === 'declining' ? '↓ Declining' : '→ Stable'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: s.vs_class_avg >= 0 ? '#1B7F3E' : '#DC2626' }}>
                              {s.vs_class_avg >= 0 ? '+' : ''}{s.vs_class_avg}%
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: '12px' }}>{s.total_topics}</td>
                          <td style={{ padding: '10px 12px', fontSize: '12px' }}>{fmt(s.avg_daily_seconds)}</td>
                        </tr>
                      )) : (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: '#94A3B8' }}>No velocity data yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AdminShell>
  );
};

export default AdminComparisonPage;
